import type { ComplianceSummary } from "../types";

interface Props {
  summary: ComplianceSummary;
}

const TARGET = 80;

// Inline colors — guaranteed to render regardless of Tailwind state.
const C_INK = "#111827";       // ~slate-900
const C_BODY = "#475569";      // ~slate-600
const C_GREEN = "#059669";     // emerald-600
const C_AMBER = "#D97706";     // amber-600
const C_RED = "#DC2626";       // red-600

function pctTextStyle(pct: number | null): React.CSSProperties {
  if (pct === null) return { color: C_BODY };
  if (pct >= TARGET) return { color: C_GREEN };
  if (pct >= TARGET - 10) return { color: C_AMBER };
  return { color: C_RED };
}

function pctBarStyle(pct: number | null): React.CSSProperties {
  if (pct === null) return { backgroundColor: "#CBD5E1" }; // slate-300
  if (pct >= TARGET) return { backgroundColor: "#10B981" };
  if (pct >= TARGET - 10) return { backgroundColor: "#F59E0B" };
  return { backgroundColor: "#EF4444" };
}

export default function ThreePillars({ summary }: Props) {
  const monthly = summary.monthly_compliance ?? [];
  const wk = aggregate(monthly, "weekly_on_time", "weekly_total");
  const me = aggregate(monthly, "month_end_passed", "month_end_evaluated");
  const ap = aggregate(monthly, "approval_passed", "approval_evaluated");
  // Hours-worked pillar: prefer the company YTD KPI (covers every YTD
  // week incl. the current month); fall back to the monthly aggregate.
  const hrs =
    summary.kpis?.company_ytd_hours_pct ??
    aggregate(monthly, "hours_passed", "hours_evaluated");

  return (
    <section className="rounded-2xl bg-white shadow-card border border-slate-200/70 overflow-hidden">
      <header className="px-5 pt-4 pb-2 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-bold tracking-tight" style={{ color: C_INK }}>
            What "compliant" means here
          </h2>
          <p className="text-xs mt-0.5" style={{ color: C_BODY }}>
            The four checks every timesheet is measured on. The headline
            % above is the average of these four rates; the hero card's
            "clean weeks" line shows the stricter share of person-weeks
            that passed{" "}
            <strong style={{ color: C_INK }}>all four</strong> at once.
          </p>
        </div>
        <div
          className="text-[11px] uppercase tracking-[0.15em]"
          style={{ color: C_BODY }}
        >
          {TARGET}% target
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-200">
        <Pillar
          number={1}
          title="Weekly submission"
          subtitle="Submit prior week's timesheet by 11:59 PM PT Monday."
          pct={wk}
        />
        <Pillar
          number={2}
          title="Month-end submission"
          subtitle="Submit every prior-month entry by 11:59 PM PT on the last day of the month."
          pct={me}
        />
        <Pillar
          number={3}
          title="Team-lead approval"
          subtitle="Rancho-billed hours approved by 2nd working day of the next month."
          pct={ap}
        />
        <Pillar
          number={4}
          title="Hours worked"
          subtitle="Log your allotted weekly hours - 40 for full-time, or your custom Beebole schedule."
          pct={hrs}
        />
      </div>
    </section>
  );
}

function Pillar({
  number,
  title,
  subtitle,
  pct,
}: {
  number: number;
  title: string;
  subtitle: string;
  pct: number | null;
}) {
  return (
    <div className="px-5 py-4 flex items-start gap-3">
      <div className="flex-none">
        <div
          className="w-8 h-8 rounded-full text-white text-sm font-extrabold flex items-center justify-center"
          style={{ backgroundColor: "#FF8800" }}
        >
          {number}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <div className="text-sm font-bold" style={{ color: C_INK }}>
            {title}
          </div>
          <div
            className="text-xl font-extrabold tabular-nums"
            style={pctTextStyle(pct)}
          >
            {pct === null ? "-" : `${pct.toFixed(1)}%`}
          </div>
        </div>
        <div
          className="text-xs mt-0.5 leading-snug"
          style={{ color: C_BODY }}
        >
          {subtitle}
        </div>
        <div className="mt-2 relative h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full transition-all"
            style={{
              width: `${pct === null ? 0 : Math.max(2, pct)}%`,
              ...pctBarStyle(pct),
            }}
          />
          <div
            className="absolute top-0 bottom-0 border-l border-dashed"
            style={{ left: `${TARGET}%`, borderColor: "rgba(15,23,42,0.6)" }}
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}

function aggregate(
  monthly: ComplianceSummary["monthly_compliance"],
  passedKey: "weekly_on_time" | "month_end_passed" | "approval_passed" | "hours_passed",
  totalKey: "weekly_total" | "month_end_evaluated" | "approval_evaluated" | "hours_evaluated",
): number | null {
  if (!monthly || monthly.length === 0) return null;
  const passed = monthly.reduce((acc, m) => acc + (m[passedKey] ?? 0), 0);
  const total = monthly.reduce((acc, m) => acc + (m[totalKey] ?? 0), 0);
  if (total === 0) return null;
  return Math.round((1000 * passed) / total) / 10;
}
